import React from 'react';
import { connect } from 'react-redux';

import { type SimpleCallback, useStaticUrls } from 'platform-components';
import { TEXT_MARKUP_TYPE } from 'platform-components/constants';
import { withRouterPath, InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import { bindActionCreators, Dispatch } from 'redux';

import { ConfirmationDialog } from 'common.components/confirmation-dialog/confirmation-dialog';
import * as userActions from 'common.components/user/actions';
import { getIsOnlyTeacherInCurrentClass, getClasses, getCurrentClass } from 'common.components/user/selectors';
import { getTrimmedName } from 'utils/get-trimmed-name';

import { ReduxTeacherStore, RootAction } from '../../../typings';
import * as classActions from '../actions';
import { getClassItemToArchive } from '../selectors';

import './class-archivate-class-modal.scss';

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface OwnProps {
    onConfirm: SimpleCallback;
    onReject: SimpleCallback;
    opened: boolean;
}
interface Props extends StoreProps, DispatchProps, OwnProps, WithRouterPathProps {}

export const ArchivateClassModalComponent = (props: Props) => {
    const { labClassStudents, labClasses } = useStaticUrls();
    const {
        archivateClassRequest,
        classes,
        currentClass,
        classItemToArchive,
        history,
        getRouterPath,
        onConfirm,
        recoverClassRequest,
        setClassItemToArchive,
        onReject,
        shouldWarn,
        opened,
    } = props;

    const handleConfirmArchivating = () => {
        if (!classItemToArchive) {
            return;
        }

        archivateClassRequest({
            classItem: classItemToArchive,
            rollback: () => recoverClassRequest(classItemToArchive),
        });

        if (onConfirm) {
            onConfirm();
        }

        if (currentClass?.id === classItemToArchive.id) {
            const firstNotArchivedClass = classes.find((classItem) => classItem.id !== classItemToArchive.id);
            const redirectUrl = firstNotArchivedClass
                ? getRouterPath(labClassStudents, { classId: firstNotArchivedClass.id })
                : labClasses;
            history.push(redirectUrl);
        }

        setClassItemToArchive(null);
    };

    if (!classItemToArchive) {
        return null;
    }

    const { level, name } = classItemToArchive;

    return (
        <ConfirmationDialog
            closable
            confirmLabelId="classArchiveConfirmation.confirm"
            headerId="classArchiveConfirmation.title"
            headerType={TEXT_MARKUP_TYPE.HTML}
            headerValues={{ name: getTrimmedName(name), level }}
            messageId={shouldWarn ? 'classArchiveConfirmation.messageWarn' : 'classArchiveConfirmation.message'}
            messageType={TEXT_MARKUP_TYPE.HTML}
            messageValues={{ level }}
            mix="archivate-class-confirmation-dialog"
            onClose={onReject}
            onConfirm={handleConfirmArchivating}
            onReject={onReject}
            opened={opened}
            rejectLabelId="classArchiveConfirmation.cancel"
            theme={ConfirmationDialog.THEMES.YANDEX}
        />
    );
};

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: WithRouterPathProps) => {
    const {
        match: {
            params: { classId },
        },
    } = ownProps;

    return {
        classes: getClasses(storeState),
        currentClass: getCurrentClass(storeState, { classId }),
        classItemToArchive: getClassItemToArchive(storeState),
        shouldWarn: !getIsOnlyTeacherInCurrentClass(storeState, { classId }),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => {
    const { archivateClassRequest, recoverClassRequest } = userActions;
    const { setClassItemToArchive } = classActions;

    return bindActionCreators(
        {
            archivateClassRequest,
            recoverClassRequest,
            setClassItemToArchive,
        },
        dispatch
    );
};

export const ArchivateClassModal = withRouterPath(
    connect(mapStateToProps, mapDispatchToProps)(ArchivateClassModalComponent)
);
